import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlaygroundChat } from '@/components/playground/PlaygroundChat';
import { cn } from '@/lib/utils';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: any[];
  isStreaming?: boolean;
}

interface IDEChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  width: number;
  onWidthChange: (width: number) => void;

  // Chat data
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onStopGeneration: () => void;
  onClearMessages: () => void;
  disabled?: boolean;
}

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 350;

export function IDEChatPanel({
  isOpen,
  onClose,
  width,
  onWidthChange,
  messages,
  isLoading,
  onSendMessage,
  onStopGeneration,
  onClearMessages,
  disabled = false,
}: IDEChatPanelProps) {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width || DEFAULT_WIDTH;
  }, [width]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Dragging left edge, so moving left increases width
      const delta = startXRef.current - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidthRef.current + delta));
      onWidthChange(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onWidthChange]);

  if (!isOpen) return null;

  return (
    <div
      className="border-l bg-background flex flex-col shrink-0"
      style={{ width: `${width}px` }}
    >
      {/* Resize Handle */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize flex items-center justify-center hover:bg-muted/50 transition-colors z-10',
          isDragging && 'bg-primary/20'
        )}
        onMouseDown={handleMouseDown}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 hover:opacity-100" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
        <span className="text-sm font-medium">Chat with Agent</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Chat Content */}
      <div className="flex-1 overflow-hidden">
        <PlaygroundChat
          messages={messages}
          isLoading={isLoading}
          onSendMessage={onSendMessage}
          onStopGeneration={onStopGeneration}
          onClearMessages={onClearMessages}
          disabled={disabled}
          className="h-full"
        />
      </div>
    </div>
  );
}

export default IDEChatPanel;
